import { FC, useState } from 'react'
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { Activity, OutdoorEngagement } from '../../../../api/models'
import { EngagementTag } from './EngagementTag'
import SpecialistIcon from '../../../../assets/images/specialist-icon.svg?react'
import AlarmTriggeredIcon from '../../../../assets/images/alarm-trigger-icon.svg?react'
import CopyIcon from '../../../../assets/images/copy-icon.svg?react'
import DropDownArrow from '../../../../assets/images/drop-down-arrow.svg?react'
import NavigateTo from '../../../../assets/images/navigate-to-icon.svg?react'
import { FormType, FormURLMap, ModeType } from '../EngagementDetailsEnums'
import { formatIsoTimestampForLocation, ISO_DATE_FORMAT } from '../../../../utils/dateUtils'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { useLocation as useRouterLocation, useParams } from 'react-router-dom'
import { Dropdown } from './Dropdown'
import { ResolveFormDialog } from './ResolveFormDialog'
import { useSpecialists, useLocation as useApiLocation } from '../../../../api/apiQueries'
import { getResolvedSpecialistIdentities, getSpecialistLabel, getSpecialistNames } from '../../../../utils/specialistUtils'
import { ModeDropdown } from './ModeDropdown'
import { formatEngagementDate } from '../ertUtils'

interface EngagementReviewInformationBoxProps {
  engagement: OutdoorEngagement
  activities: Activity[]
  currentMode: ModeType
}

const isFormType = (value: ModeType | FormType): value is FormType => {
  return Object.values(FormType).includes(value as FormType)
}

const getBaseFormUrl = (formType: FormType): string => {
  return FormURLMap[formType]
}

export const EngagementInfoBox: FC<EngagementReviewInformationBoxProps> = ({ engagement, activities, currentMode }) => {
  const { toast } = useSnackbar()
  const routeLocation = useRouterLocation()
  const { sid: locationId } = useParams()
  const locationQuery = useApiLocation(locationId)
  const fullUrl = `${window.location.origin}${routeLocation.pathname}${routeLocation.search}${routeLocation.hash}`
  const [showFormDropdown, setShowFormDropdown] = useState(false)
  const [showResolveDialog, setShowResolveDialog] = useState(false)
  const specialistIdentities = getResolvedSpecialistIdentities(activities)
  const specialistQueries = useSpecialists(specialistIdentities)
  const specialistNames = getSpecialistNames(specialistQueries.map(({ data }) => data))
  const isCustomerCare = currentMode === ModeType.CustomerCare

  const createFormQueryParams = (): string => {
    const eventDate = formatIsoTimestampForLocation(engagement.createdTimestamp, locationQuery.data, { format: ISO_DATE_FORMAT })
    const specialistNamesParam = specialistNames.join(', ')

    return `?entry.1842912509=${specialistNamesParam}&&entry.1853401100=${eventDate}&&entry.262002855=${fullUrl}`
  }

  const getFormEndIcon = (option: ModeType | FormType) => {
    if (isFormType(option)) {
      return <NavigateTo />
    }
    return undefined
  }

  const getFormLink = (formType: FormType): string => {
    return `${getBaseFormUrl(formType)}${createFormQueryParams()}`
  }

  const handleCopyClick = () => {
    navigator.clipboard.writeText(fullUrl)
    toast('ERT link copied to clipboard', { variant: 'success' })
  }

  const handleQAFormClick = () => {
    setShowFormDropdown(!showFormDropdown)
  }

  const handleResolveClick = () => {
    setShowResolveDialog(true)
  }

  const handleResolveDialogClose = () => {
    setShowResolveDialog(false)
  }

  const handleQAFormSelection = (formType: ModeType | FormType) => {
    if (!isFormType(formType)) {
      return
    }
    setShowFormDropdown(false)
    const formLink = getFormLink(formType)
    window.open(formLink, '_blank')
  }

  const formOptions: FormType[] = [FormType.NO_2WA, FormType.TWO_WAY_AUDIO, FormType.QUICK_CLEAR]

  return (
    <Stack direction="row" sx={{ flex: '1', alignItems: 'flex-end', justifyContent: 'space-between' }}>
      <Stack direction="row" sx={{ alignItems: 'flex-end', justifyContent: 'center' }}>
        <ModeDropdown currentMode={currentMode} />
        <Stack direction="row" sx={{ gap: '1rem', paddingLeft: '1rem', alignItems: 'flex-end' }} data-testid="engagement-info-box-tags">
          <EngagementTag label="Engagement ID" details={[engagement.id]} data-testid="info-box-tag-engagement-id" />
          <EngagementTag
            label={getSpecialistLabel(specialistNames)}
            details={specialistNames}
            icon={<SpecialistIcon />}
            data-testid="info-box-tag-specialist"
          />
          <EngagementTag
            label="Engagement Triggered"
            details={[formatEngagementDate(engagement.createdTimestamp, locationQuery?.data?.address?.timeZone)]}
            icon={<AlarmTriggeredIcon />}
            data-testid="info-box-tag-engagement-triggered"
          />
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 'var(--spacing-s)' }}>
        <Tooltip title="Copy ERT link" arrow>
          <IconButton
            aria-label="Copy ERT link"
            onClick={handleCopyClick}
            sx={{
              position: 'relative',
              fontSize: 'var(--size-xl)',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: 'var(--yellow-500-t10)'
              }
            }}
            data-testid="copy-ert-link-button"
          >
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        {isCustomerCare ? (
          <>
            <Button variant="contained" color="primary" onClick={handleResolveClick} sx={{ height: '100%' }} data-testid="resolve-button">
              <Typography sx={{ fontWeight: '700' }}>Resolve</Typography>
            </Button>
            <ResolveFormDialog isOpen={showResolveDialog} onClose={handleResolveDialogClose} />
          </>
        ) : (
          <Box sx={{ position: 'relative', height: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleQAFormClick()}
              sx={{
                height: '100%',
                gap: 'var(--spacing-2xs)'
              }}
              data-testid="qa-form-button"
            >
              <Typography sx={{ fontWeight: '700' }}>QA Form</Typography>
              <DropDownArrow style={{ height: 'var(--icons-m)', width: 'var(--icons-m)' }} />
            </Button>
            {showFormDropdown && (
              <Box sx={{ position: 'absolute', top: '3.5rem', right: '11rem', marginTop: '0.5rem' }}>
                <Dropdown
                  prompt="Open a QA Form"
                  options={formOptions}
                  onSelect={handleQAFormSelection}
                  getEndIcon={getFormEndIcon}
                  data-testid="qa-form-dropdown"
                />
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  )
}
